import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs/Subscription';

import { FORM_API_VALIDATION_ERROR_KEY, FormApiErrorsPayload } from './form-api-errors.model';

export { FORM_API_VALIDATION_ERROR_KEY, FormApiErrorsPayload } from './form-api-errors.model';

@Injectable()
export class FormApiValidationService {

  constructor(private translate: TranslateService) {}

  translateApiMessages(messages: string[]): string[] {
    if (!messages || !messages.length) {
      return [];
    }
    return messages.map((m) => this.translate.instant(m));
  }

  translateApiMessage(message: string): string {
    if (!message) {
      return message;
    }
    return this.translate.instant(message);
  }

  clearApiErrorOnControl(c: AbstractControl): void {
    if (!c || !c.errors || !c.errors[FORM_API_VALIDATION_ERROR_KEY]) {
      return;
    }
    const next = Object.assign({}, c.errors);
    delete next[FORM_API_VALIDATION_ERROR_KEY];
    c.setErrors(Object.keys(next).length ? next : null);
  }

  clearApiFieldErrors(form: FormGroup): void {
    if (!form) {
      return;
    }
    Object.keys(form.controls).forEach((key) => {
      const c = form.get(key);
      if (!c || !c.errors || !c.errors[FORM_API_VALIDATION_ERROR_KEY]) {
        return;
      }
      const next = Object.assign({}, c.errors);
      delete next[FORM_API_VALIDATION_ERROR_KEY];
      c.setErrors(Object.keys(next).length ? next : null);
    });
  }

  applyApiChildrenErrors(form: FormGroup, children: { [key: string]: string[] }): void {
    Object.keys(children).forEach((field) => {
      const msgs = children[field];
      if (!msgs || !msgs.length) {
        return;
      }
      const control = form.get(field);
      if (!control) {
        return;
      }
      const msg = msgs.join(' ');
      const next = Object.assign({}, control.errors || {}, { [FORM_API_VALIDATION_ERROR_KEY]: msg });
      control.setErrors(next);
      control.markAsTouched();
    });
  }

  syncFormApiErrors(form: FormGroup, payload: FormApiErrorsPayload | null): string[] {
    if (!form) {
      return [];
    }
    this.clearApiFieldErrors(form);
    if (!payload) {
      return [];
    }
    if (payload.children) {
      this.applyApiChildrenErrors(form, payload.children);
    }
    return (payload.global && payload.global.slice()) || [];
  }

  attachClearApiValidationOnValueChange(form: FormGroup): Subscription[] {
    if (!form) {
      return [];
    }
    const subs: Subscription[] = [];
    Object.keys(form.controls).forEach((key) => {
      const c = form.get(key);
      subs.push(
        c.valueChanges.subscribe(() => {
          this.clearApiErrorOnControl(c);
        })
      );
    });
    return subs;
  }

  unsubscribeAll(subs: Subscription[]): void {
    subs.forEach((s) => s.unsubscribe());
    subs.length = 0;
  }

  extractPayloadFromHttpThrownError(error: any): FormApiErrorsPayload | null {
    const body = error && error.body;
    if (!body || !body.errors) {
      return null;
    }
    return {
      children: body.errors.children || {},
      global: body.errors.global || [],
      responseMessage: body.message != null ? String(body.message) : undefined,
      responseDetails: body.details != null ? String(body.details) : undefined
    };
  }

  touchAllFormControls(form: FormGroup): void {
    if (!form) {
      return;
    }
    Object.keys(form.controls).forEach((key) => {
      const c = form.get(key);
      if (c) {
        c.markAsTouched();
      }
    });
    form.updateValueAndValidity();
  }

  normalizeDialCode(nationalDigits: string, newDial: string) {
    if (newDial === '229') {
      if (!nationalDigits.startsWith('01')) {
        nationalDigits = '01' + nationalDigits;
      }else if(nationalDigits.startsWith('01')) {
        nationalDigits = nationalDigits;
      }
    } else {
      if(nationalDigits.startsWith('01')) {
        nationalDigits = nationalDigits.slice(2);
      }else{
        nationalDigits = nationalDigits;
      }
    }
    return nationalDigits;
  }

  attachBeninPhonePrefixOnInput(
    phoneControl: AbstractControl,
    setSelectedDialCode: (prefix: string) => void
  ): Subscription {
    return phoneControl.valueChanges.subscribe((value: any) => {
      if (value && value.number === '+229') {
        const prefix = '+22901';
        setSelectedDialCode(prefix);
        phoneControl.reset();
        phoneControl.setValue(prefix);
      }
    });
  }

  preventDeletePrefix(event: KeyboardEvent , selectedDialCode: string) {

    if (!selectedDialCode.startsWith('+229')) {
      return;
    }

    const input = event.target as HTMLInputElement;

    const cursor = input.selectionStart ;

    if (
      (event.keyCode == 8 && cursor <= 6) ||
      (event.keyCode == 46 && cursor < 6)
    ) {
      event.preventDefault();
    }
  }
}



// WEBPACK FOOTER //
// C:/Users/DENISE/Documents/IT4U/web-callcenter/src/app/shared/forms/form-api-validation.service.ts